class MonteraProduct extends HTMLElement {
  constructor() {
    super();
    this.abortController = new AbortController();
    this.activeGalleryIndex = 0;
    this.touchStartX = null;
  }

  connectedCallback() {
    const signal = this.abortController.signal;

    this.addEventListener('click', this.handleClick, { signal });
    this.addEventListener('touchstart', this.handleTouchStart, { signal, passive: true });
    this.addEventListener('touchend', this.handleTouchEnd, { signal, passive: true });
    this.addEventListener('keydown', this.handleKeydown, { signal });
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const colorLink = target.closest('[data-montera-product-link]');
    if (colorLink instanceof HTMLAnchorElement) {
      if (colorLink.getAttribute('aria-current') === 'true') {
        event.preventDefault();
        this.scrollProductIntoView();
        return;
      }

      if (!event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
        event.preventDefault();
        this.loadProduct(colorLink.href);
      }
      return;
    }

    const galleryButton = target.closest('[data-gallery-index]');
    if (galleryButton instanceof HTMLElement) {
      event.preventDefault();
      this.selectGallery(Number(galleryButton.dataset.galleryIndex || 0));
      return;
    }

    const previous = target.closest('[data-gallery-previous]');
    if (previous) {
      event.preventDefault();
      this.selectGallery(this.activeGalleryIndex - 1);
      return;
    }

    const next = target.closest('[data-gallery-next]');
    if (next) {
      event.preventDefault();
      this.selectGallery(this.activeGalleryIndex + 1);
      return;
    }

    const sizeButton = target.closest('[data-variant-id]');
    if (sizeButton instanceof HTMLButtonElement) {
      event.preventDefault();
      this.selectVariant(sizeButton);
      return;
    }

    if (target.closest('[data-size-guide-open]')) {
      event.preventDefault();
      this.openSizeGuide();
      return;
    }

    if (target.closest('[data-size-guide-close]') || target.matches('[data-size-guide-modal]')) {
      event.preventDefault();
      this.closeSizeGuide();
    }
  };

  handleKeydown = (event) => {
    if (event.key === 'Escape') this.closeSizeGuide();
  };

  handleTouchStart = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('[data-product-stage]')) return;
    this.touchStartX = event.changedTouches[0]?.clientX ?? null;
  };

  handleTouchEnd = (event) => {
    if (this.touchStartX === null) return;
    const endX = event.changedTouches[0]?.clientX ?? this.touchStartX;
    const distance = endX - this.touchStartX;
    this.touchStartX = null;
    if (Math.abs(distance) < 44) return;
    this.selectGallery(this.activeGalleryIndex + (distance < 0 ? 1 : -1));
  };

  selectGallery(index) {
    const frames = Array.from(this.querySelectorAll('[data-gallery-frame]'));
    const thumbs = Array.from(this.querySelectorAll('[data-gallery-index]'));
    if (!frames.length) return;

    const normalized = (index + frames.length) % frames.length;
    this.activeGalleryIndex = normalized;

    frames.forEach((frame, frameIndex) => {
      frame.classList.toggle('is-active', frameIndex === normalized);
      frame.toggleAttribute('aria-hidden', frameIndex !== normalized);
    });

    thumbs.forEach((thumb, thumbIndex) => {
      thumb.classList.toggle('is-active', thumbIndex === normalized);
      thumb.setAttribute('aria-pressed', thumbIndex === normalized ? 'true' : 'false');
    });

    const counter = this.querySelector('[data-gallery-counter]');
    if (counter) counter.textContent = `${String(normalized + 1).padStart(2, '0')} — ${String(frames.length).padStart(2, '0')}`;
  }

  selectVariant(button) {
    if (button.disabled) return;

    const variantInput = this.querySelector('input[name="id"]');
    if (variantInput instanceof HTMLInputElement) variantInput.value = button.dataset.variantId || '';

    this.querySelectorAll('[data-variant-id]').forEach((item) => {
      const selected = item === button;
      item.classList.toggle('is-selected', selected);
      item.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    const submit = this.querySelector('[data-montera-add]');
    if (submit instanceof HTMLButtonElement) {
      submit.disabled = false;
      submit.classList.add('is-ready');
      const label = submit.querySelector('[data-add-label]');
      const price = submit.querySelector('[data-add-price]');
      if (label) label.textContent = submit.dataset.readyLabel || 'AGGIUNGI AL CARRELLO';
      if (price && button.dataset.variantPrice) price.textContent = `${button.dataset.variantPrice} ↗`;
    }
  }

  async loadProduct(href) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId || this.classList.contains('is-loading')) {
      window.location.assign(href);
      return;
    }

    this.classList.add('is-loading');

    try {
      const url = new URL(href, window.location.origin);
      url.searchParams.set('section_id', sectionId);
      const response = await fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error(`Product request failed: ${response.status}`);

      const markup = await response.text();
      const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
      const nextProduct = documentFragment.querySelector('montera-product');
      if (!(nextProduct instanceof HTMLElement)) throw new Error('Product section was not returned');

      const currentSection = this.closest('.shopify-section');
      const nextSection = nextProduct.closest('.shopify-section');

      if (currentSection && nextSection) {
        currentSection.replaceWith(nextSection);
      } else {
        this.replaceWith(nextProduct);
      }

      const cleanUrl = new URL(href, window.location.origin);
      history.pushState({ monteraProduct: true }, '', cleanUrl.pathname + cleanUrl.search);
      document.title = nextProduct.dataset.pageTitle || document.title;

      requestAnimationFrame(() => {
        const mountedProduct = document.querySelector('montera-product');
        mountedProduct?.scrollProductIntoView?.();
      });
    } catch (error) {
      console.warn('[Montera] Switching product with a full navigation fallback.', error);
      window.location.assign(href);
    }
  }

  scrollProductIntoView() {
    if (!window.matchMedia('(max-width: 760px)').matches) return;
    const stage = this.querySelector('[data-product-stage]');
    if (!stage) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const top = stage.getBoundingClientRect().top + window.scrollY - 8;
    window.scrollTo({ top: Math.max(0, top), behavior: reduced ? 'auto' : 'smooth' });
  }

  openSizeGuide() {
    const modal = this.querySelector('[data-size-guide-modal]');
    if (!modal) return;
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    modal.querySelector('[data-size-guide-close]')?.focus();
  }

  closeSizeGuide() {
    const modal = this.querySelector('[data-size-guide-modal]');
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
  }
}

if (!customElements.get('montera-product')) {
  customElements.define('montera-product', MonteraProduct);
}

if (!window.__monteraPopstateBound) {
  window.__monteraPopstateBound = true;
  window.addEventListener('popstate', () => window.location.reload());
}
